/* Tracks, the calendar, and the placeholder pages the brief asks to reserve
 * space for. Grouped because each is short. */

import { Link, useParams } from 'react-router-dom'
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CalendarDays, CloudSun, MapPin, Radio, Loader2, Check } from 'lucide-react'
import { api } from '../lib/api'
import type {
  BestByClass, ResolveStationResult, StationHit,
  TrackDetail as TrackDetailType, TrackRow, User,
} from '../lib/api'
import { lapTime, shortDate } from '../lib/format'
import { Empty, Spinner } from '../components/bits'

/** Best lap per class, stacked. "unknown" is shown rather than hidden - a
 *  session nobody tagged is a gap in the record, not a Cadet 9 lap. */
function ClassBests({ bests }: { bests: BestByClass | undefined }) {
  const entries = Object.entries(bests ?? {})
  if (entries.length === 0) return <span className="dim">—</span>
  // Classified first, unknown last: it is the caveat, not the headline.
  entries.sort(([a], [b]) =>
    (a === 'unknown' ? 1 : 0) - (b === 'unknown' ? 1 : 0) || a.localeCompare(b))
  return (
    <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 3 }}>
      {entries.map(([cls, best]) => (
        <span key={cls} className="row" style={{ gap: 7, justifyContent: 'flex-end' }}>
          <span className={cls === 'unknown' ? 'tiny dim' : 'tiny muted'}>
            {cls === 'unknown' ? 'unclassified' : cls}
          </span>
          <span className={cls === 'unknown' ? 'lap dim' : 'lap best'}>
            {lapTime(best)}
          </span>
        </span>
      ))}
    </span>
  )
}

export function Tracks() {
  const q = useQuery({
    queryKey: ['tracks'],
    queryFn: () => api.get<{ tracks: TrackRow[] }>('/api/tracks'),
  })
  if (q.isLoading) return <div className="page"><Spinner /></div>
  const tracks = q.data?.tracks ?? []

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Tracks</h1>
          <div className="sub">Every circuit the app holds session data for</div>
        </div>
      </div>
      {tracks.length === 0 ? (
        <Empty title="No tracks yet"
          body="Tracks are created automatically from the venue names Race Studio records, as sessions arrive." />
      ) : (
        <div className="grid three-col">
          {tracks.map((t) => (
            <Link to={`/tracks/${t.slug}`} key={t.id} className="card"
              style={{ textDecoration: 'none', color: 'inherit' }}>
              <h2>{t.name}</h2>
              <div className="row small muted" style={{ gap: 6, marginTop: 4 }}>
                <MapPin size={13} /> {t.state || t.country}
              </div>
              <div className="row tiny" style={{ gap: 6, marginTop: 4,
                color: t.weather_station ? 'var(--muted)' : 'var(--amber)' }}>
                <CloudSun size={12} />
                {t.weather_station ? t.weather_station : 'no weather station set'}
              </div>
              <div className="between" style={{ marginTop: 14 }}>
                <div>
                  <div className="tiny dim">Sessions</div>
                  <div style={{ fontWeight: 650 }}>{t.sessions}</div>
                </div>
                <div>
                  <div className="tiny dim">Best</div>
                  <div className="lap best">{lapTime(t.best_lap_s)}</div>
                </div>
                <div>
                  <div className="tiny dim">Last</div>
                  <div style={{ fontWeight: 650 }}>{shortDate(t.last_visited)}</div>
                </div>
              </div>
              <div className="row wrap" style={{ gap: 6, marginTop: 12 }}>
                {t.layouts.map((l) => (
                  <span className="badge" key={l.id}>{l.name}</span>
                ))}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}

function WeatherStation({ slug, track, canEdit }: {
  slug: string; track: TrackDetailType; canEdit: boolean
}) {
  const qc = useQueryClient()
  const [address, setAddress] = useState(track.address ?? '')
  const [found, setFound] = useState<StationHit | null>(null)
  const [coords, setCoords] = useState<{ lat: number | null; lon: number | null }>(
    { lat: track.latitude, lon: track.longitude })

  const resolve = useMutation({
    mutationFn: () => api.post<ResolveStationResult>(
      `/api/tracks/${slug}/resolve-station`, { address: address || null }),
    onSuccess: (r) => {
      setFound(r.station)
      setCoords({ lat: r.latitude, lon: r.longitude })
    },
  })

  const save = useMutation({
    mutationFn: () => api.patch(`/api/tracks/${slug}`, {
      address: address || null,
      latitude: coords.lat, longitude: coords.lon,
      weather_station: found?.name ?? null,
      weather_wmo: found?.wmo ?? null,
      weather_product: found?.product ?? null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['track', slug] })
      qc.invalidateQueries({ queryKey: ['tracks'] })
      setFound(null)
    },
  })

  const current = track.weather_station

  return (
    <div className="card" style={{ marginBottom: 14 }}>
      <div className="card-head"><h2>Weather station</h2></div>
      <p className="small dim" style={{ marginTop: 0 }}>
        The jetting advisor pulls live air density from the Bureau station chosen
        here. It fills in automatically from the track's GPS; set it once and it's
        saved for good.
      </p>

      <div className="row" style={{ gap: 8, alignItems: 'center', marginBottom: 10 }}>
        <CloudSun size={16} style={{ opacity: .7 }} />
        {current ? (
          <span>Currently using <b>{current}</b></span>
        ) : (
          <span className="muted">No station set yet — it'll be picked from GPS.</span>
        )}
      </div>

      {!canEdit ? null : (
        <>
          <label className="tiny dim">Track address (optional — GPS is used if blank)</label>
          <input value={address} onChange={(e) => setAddress(e.target.value)}
            placeholder="e.g. Greer Park Raceway, Snellings Rd, Helidon Spa QLD"
            style={{ width: '100%', marginBottom: 10 }} />

          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <button className="btn ghost" disabled={resolve.isPending}
              onClick={() => resolve.mutate()}>
              {resolve.isPending ? <Loader2 size={15} className="spin" /> : <Radio size={15} />}
              Get closest weather station
            </button>
            {found ? (
              <button className="btn" disabled={save.isPending}
                onClick={() => save.mutate()}>
                {save.isPending ? <Loader2 size={15} className="spin" /> : <Check size={15} />}
                Save this track
              </button>
            ) : null}
          </div>

          {resolve.isSuccess && !found ? (
            <div className="small" style={{ color: 'var(--amber)', marginTop: 10 }}>
              No Bureau station close enough to that location — check the address,
              or this track's GPS may be off.
            </div>
          ) : null}
          {found ? (
            <div className="card" style={{ marginTop: 10, background: 'var(--hover)' }}>
              <div className="between">
                <div>
                  <div className="tiny dim">Nearest station</div>
                  <div style={{ fontWeight: 650 }}>{found.name}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div className="tiny dim">Distance</div>
                  <div style={{ fontWeight: 650 }}>{found.distance_km} km</div>
                </div>
              </div>
              <div className="tiny muted" style={{ marginTop: 6 }}>
                Press “Save this track” to lock it in.
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  )
}


export function TrackDetail({ user }: { user: User }) {
  const { slug } = useParams()
  const q = useQuery({
    queryKey: ['track', slug],
    queryFn: () => api.get<TrackDetailType>(`/api/tracks/${slug}`),
  })
  if (q.isLoading) return <div className="page"><Spinner /></div>
  if (!q.data) return <div className="page"><Empty title="Track not found" body="" /></div>
  const t = q.data

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>{t.name}</h1>
          <div className="sub">{[t.location, t.state].filter(Boolean).join(', ')}</div>
        </div>
      </div>
      <WeatherStation slug={slug!} track={t} canEdit={user.is_owner} />

      <div className="card">
        <div className="card-head"><h2>Layouts</h2></div>
        <p className="small dim" style={{ marginTop: 0 }}>
          Kept separate on purpose. A reversed circuit is not the same corners,
          so a best lap from one says nothing about the other.
        </p>
        <div className="scroll-x">
          <table>
            <thead>
              <tr><th>Layout</th><th>Direction</th><th className="num">Sessions</th>
                <th className="num">Best by class</th><th className="num">Last visited</th></tr>
            </thead>
            <tbody>
              {t.layouts.map((l) => (
                <tr key={l.id}>
                  <td>{l.name}</td>
                  <td className="muted">{l.direction}</td>
                  <td className="num">{l.sessions}</td>
                  <td className="num"><ClassBests bests={l.best_by_class} /></td>
                  <td className="num muted">{shortDate(l.last_visited)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="small dim" style={{ marginBottom: 0, marginTop: 12 }}>
          Classes are never merged. Cadet 12 runs without the 16 mm restrictor
          and is around a second a lap quicker, so one combined best would just
          be the Cadet 12 time standing in for both.
        </p>
      </div>
    </div>
  )
}

export function Weather() {
  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Weather</h1>
          <div className="sub">Air density decides the jet. This is where the
            station will feed in.</div>
        </div>
      </div>
      <Empty title="No weather station connected yet"
        body="Once a station is mounted and pushing readings, each session records the air it ran in — and the jetting history becomes something the app can learn from rather than guess at." />
      <div className="card" style={{ marginTop: 14 }}>
        <div className="row small muted" style={{ gap: 8 }}>
          <CloudSun size={16} />
          Track temperature will still need entering by hand — a station in the
          paddock measures the paddock, not the racing line.
        </div>
      </div>
    </div>
  )
}

export function Placeholder({ title, body }: { title: string; body: string }) {
  return (
    <div className="page">
      <div className="page-head"><div><h1>{title}</h1></div></div>
      <Empty title="Not built yet" body={body} />
    </div>
  )
}

export function NotFound() {
  return (
    <div className="page">
      <Empty title="Page not found"
        body="That link does not go anywhere in this app."
        action={<Link className="btn" to="/"><CalendarDays size={16} /> Back to dashboard</Link>} />
    </div>
  )
}
